import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  TrendingUp, 
  TrendingDown, 
  BarChart3, 
  Lightbulb, 
  Target,
  AlertCircle,
  CheckCircle,
  Brain,
  Zap
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface InsightData {
  factor: string;
  outcome: string;
  correlation: number;
  confidence: 'low' | 'moderate' | 'high';
  insight: string;
  actionableTip: string;
  dataPoints: number;
  averageHigh: number;
  averageLow: number;
  direction: 'positive' | 'negative' | 'neutral';
}

interface BehaviorInsightsProps {
  onClose?: () => void;
}

const FACTORS = {
  'alcohol_units': 'Alcohol Consumption',
  'processed_food_level': 'Processed Food Intake',
  'exercise_duration': 'Exercise Duration',
  'sunlight_minutes': 'Sunlight Exposure',
  'water_intake': 'Hydration Level',
  'sleep_hours': 'Sleep Duration',
  'screen_time': 'Evening Screen Time'
};

const OUTCOMES = {
  'sleep_quality': 'Sleep Quality',
  'energy_level': 'Energy Level',
  'mood_score': 'Mood Rating',
  'anxiety_level': 'Anxiety Level',
  'stress_level': 'Stress Level',
  'focus_level': 'Focus & Clarity'
};

const TOP_CORRELATIONS = [
  {
    factor: 'Processed Food',
    outcome: 'Sleep Quality',
    description: 'Highly processed foods disrupt circadian rhythm and gut health'
  },
  {
    factor: 'Alcohol',
    outcome: 'Mood & Energy',
    description: 'Alcohol fragments sleep and increases next-day anxiety'
  },
  {
    factor: 'Hydration',
    outcome: 'Energy & Focus',
    description: 'Even mild dehydration reduces cognitive performance'
  },
  {
    factor: 'Exercise',
    outcome: 'Sleep & Stress',
    description: 'Movement reduces cortisol and improves sleep depth'
  },
  {
    factor: 'Sunlight',
    outcome: 'Sleep & Mood',
    description: 'Morning light regulates circadian rhythm and serotonin'
  }
];

export default function BehaviorInsights({ onClose }: BehaviorInsightsProps) {
  const [selectedFactor, setSelectedFactor] = useState<string>('');
  const [selectedOutcome, setSelectedOutcome] = useState<string>('');
  const [insights, setInsights] = useState<InsightData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [timeframe, setTimeframe] = useState('30'); // days
  const [hasEnoughData, setHasEnoughData] = useState(true);

  useEffect(() => {
    checkDataAvailability();
  }, [timeframe]);

  const checkDataAvailability = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const daysAgo = new Date();
      daysAgo.setDate(daysAgo.getDate() - parseInt(timeframe));

      const { data, error } = await supabase
        .from('daily_checkins')
        .select('date, sleep_quality, energy_level, processed_food_level, water_intake, exercise_duration, sunlight_minutes')
        .eq('user_id', user.id)
        .gte('date', daysAgo.toISOString().split('T')[0])
        .order('date', { ascending: false });

      if (error) throw error;

      const dataPoints = data?.length || 0;
      setHasEnoughData(dataPoints >= 7); // Need at least a week of data

      // Auto-generate top insights if we have enough data
      if (dataPoints >= 14) {
        await generateTopInsights(data);
      }
    } catch (error) {
      console.error('Error checking data availability:', error);
    }
  };

  const generateTopInsights = async (data: any[]) => {
    const generatedInsights: InsightData[] = [];

    // Processed Food ↔ Sleep Quality
    const processedFoodInsight = analyzeCorrelation(
      data,
      'processed_food_level',
      'sleep_quality',
      'Processed Food',
      'Sleep Quality'
    );
    if (processedFoodInsight) generatedInsights.push(processedFoodInsight);

    // Exercise ↔ Energy
    const exerciseInsight = analyzeCorrelation(
      data,
      'exercise_duration',
      'energy_level',
      'Exercise',
      'Energy Level'
    );
    if (exerciseInsight) generatedInsights.push(exerciseInsight);

    // Sunlight ↔ Sleep Quality
    const sunlightInsight = analyzeCorrelation(
      data,
      'sunlight_minutes',
      'sleep_quality',
      'Sunlight',
      'Sleep Quality'
    );
    if (sunlightInsight) generatedInsights.push(sunlightInsight);

    // Hydration ↔ Energy
    const hydrationInsight = analyzeCorrelation(
      data,
      'water_intake',
      'energy_level',
      'Hydration',
      'Energy Level'
    );
    if (hydrationInsight) generatedInsights.push(hydrationInsight);

    setInsights(generatedInsights);
  };

  const analyzeCorrelation = (
    data: any[],
    factorField: string,
    outcomeField: string,
    factorName: string,
    outcomeName: string
  ): InsightData | null => {
    // Filter out entries without both values
    const validData = data.filter(entry => 
      entry[factorField] !== null && 
      entry[outcomeField] !== null
    );

    if (validData.length < 7) return null;

    // Split data into high and low factor groups
    const sortedByFactor = validData.sort((a, b) => (b[factorField] || 0) - (a[factorField] || 0));
    const topThird = sortedByFactor.slice(0, Math.floor(validData.length / 3));
    const bottomThird = sortedByFactor.slice(-Math.floor(validData.length / 3));

    const avgHighFactor = topThird.reduce((sum, entry) => sum + (entry[outcomeField] || 0), 0) / topThird.length;
    const avgLowFactor = bottomThird.reduce((sum, entry) => sum + (entry[outcomeField] || 0), 0) / bottomThird.length;

    const difference = avgHighFactor - avgLowFactor;
    const correlation = Math.abs(difference) / 10; // Normalize to 0-1 scale

    // Determine confidence based on data points and effect size
    let confidence: 'low' | 'moderate' | 'high' = 'low';
    if (validData.length >= 21 && Math.abs(difference) >= 1.5) confidence = 'high';
    else if (validData.length >= 14 && Math.abs(difference) >= 1.0) confidence = 'moderate';

    const direction = difference > 0 ? 'positive' : difference < 0 ? 'negative' : 'neutral';

    // Generate insight text
    const insight = generateInsightText(factorName, outcomeName, difference, avgHighFactor, avgLowFactor);
    const actionableTip = generateActionableTip(factorName, outcomeName, direction);

    return {
      factor: factorName,
      outcome: outcomeName,
      correlation,
      confidence,
      insight,
      actionableTip,
      dataPoints: validData.length,
      averageHigh: avgHighFactor,
      averageLow: avgLowFactor,
      direction
    };
  };

  const generateInsightText = (
    factor: string,
    outcome: string,
    difference: number,
    avgHigh: number,
    avgLow: number
  ): string => {
    const direction = difference > 0 ? 'higher' : 'lower';
    const magnitude = Math.abs(difference);

    if (magnitude < 0.5) {
      return `${factor} shows minimal impact on your ${outcome}.`;
    } else if (magnitude < 1.5) {
      return `When ${factor} is high, your ${outcome} tends to be ${direction} (${Math.abs(difference).toFixed(1)} points on average).`;
    } else {
      return `Strong pattern detected: High ${factor} is linked to ${direction} ${outcome} (${Math.abs(difference).toFixed(1)} point difference).`;
    }
  };

  const generateActionableTip = (factor: string, outcome: string, direction: 'positive' | 'negative' | 'neutral'): string => {
    const tips: Record<string, Record<string, string>> = {
      'Processed Food': {
        'Sleep Quality': 'Try reducing processed foods at dinner - your body may rest more deeply.',
        'Energy Level': 'Swap one processed snack daily for whole foods to boost your energy.'
      },
      'Exercise': {
        'Sleep Quality': 'Even 20 minutes of movement can improve your sleep by 30+ minutes.',
        'Energy Level': 'Morning movement tends to create sustained energy throughout the day.'
      },
      'Sunlight': {
        'Sleep Quality': 'Getting sunlight in the first hour after waking could improve your sleep.',
        'Energy Level': '10-15 minutes of morning sunlight can boost alertness and mood.'
      },
      'Hydration': {
        'Energy Level': 'Adding 2 more glasses of water daily could boost your energy levels.',
        'Sleep Quality': 'Stay hydrated during the day, but reduce intake 2 hours before bed.'
      }
    };

    return tips[factor]?.[outcome] || `Notice how ${factor} affects your ${outcome} and experiment with adjustments.`;
  };

  const analyzeCustomCorrelation = async () => {
    if (!selectedFactor || !selectedOutcome) return;

    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const daysAgo = new Date();
      daysAgo.setDate(daysAgo.getDate() - parseInt(timeframe));

      // Get data based on selected factor and outcome
      const { data, error } = await supabase
        .from('daily_checkins')
        .select('*')
        .eq('user_id', user.id)
        .gte('date', daysAgo.toISOString().split('T')[0]);

      if (error) throw error;

      const factorName = FACTORS[selectedFactor as keyof typeof FACTORS];
      const outcomeName = OUTCOMES[selectedOutcome as keyof typeof OUTCOMES];

      const insight = analyzeCorrelation(data, selectedFactor, selectedOutcome, factorName, outcomeName);
      if (insight) {
        setInsights([insight, ...insights.filter(i => i.factor !== factorName || i.outcome !== outcomeName)]);
      }
    } catch (error) {
      console.error('Error analyzing correlation:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getConfidenceColor = (confidence: string) => {
    switch (confidence) {
      case 'high': return 'text-accent bg-accent/10';
      case 'moderate': return 'text-primary bg-primary/10';
      case 'low': return 'text-destructive bg-destructive/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getDirectionIcon = (direction: string) => {
    switch (direction) {
      case 'positive': return <TrendingUp className="w-4 h-4 text-accent" />;
      case 'negative': return <TrendingDown className="w-4 h-4 text-destructive" />;
      default: return <BarChart3 className="w-4 h-4 text-muted-foreground" />;
    }
  };

  if (!hasEnoughData) {
    return (
      <Card className="p-8 text-center">
        <Brain className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-2xl font-bold mb-4">Building Your Insights</h3>
        <p className="text-muted-foreground mb-6">
          Keep logging your daily check-ins! You need at least 7 days of data to generate meaningful behavioural insights. 
          The more data you provide, the more personalised and accurate your insights become.
        </p>
        <div className="bg-muted/50 p-4 rounded-lg">
          <p className="text-sm">
            💡 <strong>Coming soon:</strong> Discover how your sleep, food, exercise, and other factors 
            impact your mood, energy, and overall wellbeing.
          </p>
        </div>
        {onClose && (
          <Button variant="outline" onClick={onClose} className="mt-6">
            Continue Daily Tracking
          </Button>
        )}
      </Card>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-3xl font-bold flex items-center gap-3">
              <Zap className="w-8 h-8 text-primary" />
              Behaviour Insights
            </h2>
            <p className="text-muted-foreground mt-2">
              Discover how your habits impact your wellbeing
            </p>
          </div>
          {onClose && (
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          )}
        </div>

        <Tabs defaultValue="insights" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="insights">Your Patterns</TabsTrigger>
            <TabsTrigger value="explore">Explore Connections</TabsTrigger>
            <TabsTrigger value="tips">Science-Backed Tips</TabsTrigger>
          </TabsList>

          <TabsContent value="insights" className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold">Patterns Detected</h3>
              <Select value={timeframe} onValueChange={setTimeframe}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="14">2 weeks</SelectItem>
                  <SelectItem value="30">1 month</SelectItem>
                  <SelectItem value="60">2 months</SelectItem>
                  <SelectItem value="90">3 months</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {insights.length === 0 ? (
              <Card className="p-8 text-center">
                <BarChart3 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  No significant patterns detected yet. Keep tracking for more insights!
                </p>
              </Card>
            ) : (
              <div className="grid gap-4">
                {insights.map((insight, index) => (
                  <Card key={index} className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        {getDirectionIcon(insight.direction)}
                        <div>
                          <h4 className="font-semibold text-lg">
                            {insight.factor} ↔ {insight.outcome}
                          </h4>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge className={getConfidenceColor(insight.confidence)}>
                              {insight.confidence} confidence
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {insight.dataPoints} data points
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold">
                          {(insight.correlation * 100).toFixed(0)}%
                        </div>
                        <div className="text-xs text-muted-foreground">correlation</div>
                      </div>
                    </div>

                    <p className="text-sm text-muted-foreground mb-4">
                      {insight.insight}
                    </p>

                    <div className="bg-gradient-therapy p-4 rounded-lg border border-secondary/30">
                      <div className="flex items-center gap-2 mb-2">
                        <Lightbulb className="w-4 h-4 text-primary" />
                        <span className="text-sm font-medium text-primary">Actionable Insight</span>
                      </div>
                      <p className="text-sm text-foreground">{insight.actionableTip}</p>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="explore" className="space-y-6">
            <Card className="p-6">
              <h3 className="text-xl font-semibold mb-4">Explore Custom Connections</h3>
              
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="text-sm font-medium mb-2 block">Factor (Input)</label>
                  <Select value={selectedFactor} onValueChange={setSelectedFactor}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a factor" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(FACTORS).map(([key, name]) => (
                        <SelectItem key={key} value={key}>{name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Outcome (Result)</label>
                  <Select value={selectedOutcome} onValueChange={setSelectedOutcome}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select an outcome" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(OUTCOMES).map(([key, name]) => (
                        <SelectItem key={key} value={key}>{name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button
                onClick={analyzeCustomCorrelation}
                disabled={!selectedFactor || !selectedOutcome || isLoading}
                className="w-full"
              >
                {isLoading ? 'Analysing...' : 'Analyse Connection'}
              </Button>

              <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground">
                  💡 <strong>Remember:</strong> Correlation ≠ causation. These insights show patterns in your data 
                  that might be worth exploring, but individual results vary.
                </p>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="tips" className="space-y-6">
            <div className="grid gap-4">
              {TOP_CORRELATIONS.map((tip, index) => (
                <Card key={index} className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <Target className="w-5 h-5 text-primary" />
                    <h4 className="font-semibold">
                      {tip.factor} ↔ {tip.outcome}
                    </h4>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {tip.description}
                  </p>
                </Card>
              ))}
            </div>

            <Card className="p-6 bg-gradient-therapy border-primary/30">
              <div className="flex items-center gap-3 mb-3">
                <CheckCircle className="w-5 h-5 text-accent" />
                <h4 className="font-semibold text-primary">Start Small, Win Big</h4>
              </div>
              <p className="text-sm text-foreground">
                Focus on changing one habit at a time. Small, consistent changes create lasting transformation. 
                Use your personal data to guide which habit to prioritise first.
              </p>
            </Card>
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  );
}